import { forwardRef, useEffect, useState } from 'react';
import { IMaskInput } from 'react-imask';
import { useTranslation } from 'react-i18next';
import { Input, InputProps } from '../Input/Input';
import { DateTime } from 'luxon';

export type DatePickerInputProps = InputProps & {
  value?: Date,
  onChangeDate?: (value?: Date) => void,
  // Sends each keystroke's date (undefined until it is complete and valid) instead of only on blur,
  // and keeps invalid text on blur so the parent can flag it.
  sendsDateWhileTyping?: boolean,
};

interface DatePickerMaskInputProps {
  onChange: (event: { target: { name: string; value: string } }) => void,
  name: string,
}

const DatePickerMaskInput = forwardRef<HTMLElement, DatePickerMaskInputProps>(
  function TextMaskCustom (props, ref) {
    const { onChange, ...other } = props;
    return (
      <IMaskInput
        {...other}
        definitions={{ '#': /[0-9]/ }}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        inputRef={ref as any}
        mask="##/`##/`####"
        onAccept={(value) => onChange({ target: { name: props.name, value } })}
        // To prevent the warning from showing up in the console
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onChange={() => {}}
        overwrite
      />
    );
  },
);

const DateFormat = 'MM/dd/yyyy';

// An invalid Date counts as no date.
export const toComparableTime = (date?: Date) => {
  const time = date?.getTime();
  return time === undefined || isNaN(time) ? undefined : time;
};

export const DatePickerInput = ({
  onChangeDate,
  onChangeText,
  sendsDateWhileTyping,
  value,
  ...rest
}: DatePickerInputProps) => {
  const { t } = useTranslation();

  const [internalValue, setInternalValue] = useState("");
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    setInternalValue(formatDate(value));
  }, []);

  // Callers often build a new Date on every render, so compare dates by their time.
  const valueTime = toComparableTime(value);
  useEffect(() => {
    if (!focused) {
      setInternalValue(formatDate(value));
    }
  }, [valueTime]);

  useEffect(() => {
    if (!focused && !sendsDateWhileTyping) {
      setInternalValue(formatDate(value));
    }
  }, [focused]);

  const parseDate = (text: string) => {
    const date = DateTime.fromFormat(text, DateFormat);

    if (date.isValid) {
      return date.toJSDate();
    }
    return undefined;
  };

  const handleChangeText = (text: string) => {
    // The mask also reports unchanged text, e.g. when the field gains focus.
    if (text === internalValue) {
      return;
    }
    onChangeText?.(text);
    setInternalValue(text);
    if (sendsDateWhileTyping) {
      fireOnChangeDate(text);
    }
  };

  const fireOnChangeDate = (text: string) => {
    const parsedDate = !text ? undefined : parseDate(text);
    onChangeDate?.(parsedDate);
  };

  const formatDate = (date?: Date) => {
    if (toComparableTime(date) === undefined) {
      return "";
    }
    return DateTime.fromJSDate(date as Date).toFormat(DateFormat);
  };
   
  return (
    <Input
      inputComponent={DatePickerMaskInput}
      inputMode="tel"
      onBlur={() => {
        setFocused(false);
        if (!sendsDateWhileTyping) {
          fireOnChangeDate(internalValue as string);
        }
      }}
      onChangeText={handleChangeText}
      onFocus={() => {
        setFocused(true);
      }}
      placeholder={t($ => $.designSystem.datePicker.placeholder)}
      value={internalValue}
      variant="outlined"
      {...rest}
    />
  );
};
